import { type IColumnTypes } from "commonComponents/utilities/AgGridReact/AgGridReact.types";
import { standardColumnImplementations } from "commonComponents/utilities/AgGridReact/standardColumnImplementations/standardColumnImplementations";
import { GridColumnType } from "commonComponents/utilities/Grid/GridContainer.types";

import {
    ApproversCell,
    BillAccountingStatusCell,
    BillAmountCell,
    BillCurrencyCell,
    BillDocumentTypeCell,
    BillPayToCell,
    BillStatusCell,
    DueDateCell,
    LinkedScheduleItemStatusCell,
    makeBillLinkCell,
    makeLienWaiverStatusCell,
    RelatedOwnerInvoicesCell,
    RelatedPurchaseOrderLinksCell,
    SelectCell,
} from "./billAgCellRenderers";

/**
 * AG Grid column-type registry for the Bills List (ado-281381). Keyed by `GridColumnType`,
 * these are merged over `standardColumnImplementations` so Bill-specific columns (link,
 * status, approvers, etc.) render with their real components while generic columns (text,
 * date, …) fall back to the shared implementations. `mapBTColToAgCol` sets each column's
 * `type` to its `columnType`, which AG Grid resolves against this map.
 *
 * `useAbsoluteLink` (from the `billsFullPageConversion` flag) selects standalone-page vs.
 * modal-overlay links, mirroring the Kendo column factory.
 */
export const getBillAgColumnImplementations = (useAbsoluteLink: boolean): IColumnTypes => ({
    ...standardColumnImplementations,
    [GridColumnType.billLink]: {
        cellRenderer: makeBillLinkCell(useAbsoluteLink),
    },
    [GridColumnType.billAmount]: {
        cellRenderer: BillAmountCell,
    },
    // Override every currency-typed column: Bills sends currency values (Amount paid, Remaining
    // balance, and currency custom fields) as { value, scale } objects, which the standard currency
    // renderer/valueFormatter would pass straight to CurrencyDisplay (expects a number) and crash
    // the whole grid with a DecimalError. BillCurrencyCell unwraps .value for all of them.
    [GridColumnType.currency]: {
        cellRenderer: BillCurrencyCell,
    },
    [GridColumnType.currencyAccounting]: {
        cellRenderer: BillCurrencyCell,
    },
    [GridColumnType.currencyLimit]: {
        cellRenderer: BillCurrencyCell,
    },
    [GridColumnType.currencyVariableScale]: {
        cellRenderer: BillCurrencyCell,
    },
    [GridColumnType.billPayTo]: {
        cellRenderer: BillPayToCell,
    },
    [GridColumnType.billStatus]: {
        cellRenderer: BillStatusCell,
    },
    [GridColumnType.billAccountingStatus]: {
        cellRenderer: BillAccountingStatusCell,
    },
    [GridColumnType.billDocumentType]: {
        cellRenderer: BillDocumentTypeCell,
    },
    [GridColumnType.lienWaiverStatus]: {
        cellRenderer: makeLienWaiverStatusCell(useAbsoluteLink),
    },
    [GridColumnType.linkedScheduleItemStatus]: {
        cellRenderer: LinkedScheduleItemStatusCell,
    },
    [GridColumnType.relatedPurchaseOrderLinks]: {
        cellRenderer: RelatedPurchaseOrderLinksCell,
    },
    [GridColumnType.approvers]: {
        cellRenderer: ApproversCell,
    },
    [GridColumnType.dueDate]: {
        cellRenderer: DueDateCell,
    },
    [GridColumnType.select]: {
        cellRenderer: SelectCell,
    },
    [GridColumnType.relatedOwnerInvoicesLink]: {
        cellRenderer: RelatedOwnerInvoicesCell,
    },
});
